import React, { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { useClient } from '../../contexts/ClientContext';
import { useProject } from '../../contexts/ProjectContext';
import { useFilter } from '../../contexts/FilterContext';
import {
  getComputeOptimizationStats,
  getSavingsByService,
  getGroupedRecommendations,
  getGuardrailsEnvironments,
  triggerCostOptimizationScan,
} from '../../services/cloudopsApi';
import ComputeStatsCards, { ComputeStats } from './components/ComputeStatsCards';
import SavingsByServiceCards from './components/SavingsByServiceCards';
import OptimizationRecommendationsTable from './components/OptimizationRecommendationsTable';

interface ScanMessage {
  type: 'success' | 'error';
  text: string;
}

const ComputeOptimizationPage: React.FC = () => {
  const { user } = useAuth();
  const { selectedClientId } = useClient();
  const { selectedProjectId } = useProject();
  const { filterType, selectedId: filterId } = useFilter();
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [tenantId, setTenantId] = useState<string | null>(null);

  const [stats, setStats] = useState<ComputeStats | null>(null);
  const [savingsByService, setSavingsByService] = useState<Record<string, unknown>[]>([]);
  const [recommendations, setRecommendations] = useState<Record<string, unknown>[]>([]);
  const [selectedService, setSelectedService] = useState<string | null>(null);
  const [sortBy, setSortBy] = useState<string>('highest_savings');
  const [scanning, setScanning] = useState<boolean>(false);
  const [scanMessage, setScanMessage] = useState<ScanMessage | null>(null);

  // Get tenant_id from core AWS environments (same IDs used by cost optimization scanner)
  useEffect(() => {
    const fetchTenantId = async () => {
      try {
        const clientId = selectedClientId || user?.client_id || undefined;
        const environments = await getGuardrailsEnvironments(clientId);

        if ((environments?.length ?? 0) > 0) {
          setTenantId(environments[0].id);
        } else {
          setLoading(false);
        }
      } catch (err: unknown) {
        console.error('Error fetching environments:', err);
        setLoading(false);
      }
    };

    fetchTenantId();
  }, [selectedClientId, selectedProjectId, user?.client_id, filterType, filterId]);

  // Load data when tenantId is available
  useEffect(() => {
    if (tenantId) {
      loadAllData();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tenantId, selectedProjectId, selectedService, filterType, filterId]);

  const loadAllData = async () => {
    if (!tenantId) return;

    setLoading(true);
    setError(null);

    try {
      const [statsData, savingsData, recommendationsData] = await Promise.all([
        getComputeOptimizationStats(tenantId),
        getSavingsByService(tenantId),
        getGroupedRecommendations(tenantId, selectedService || null),
      ]);

      setStats(statsData);
      setSavingsByService(savingsData);
      
      // Sort recommendations
      let sortedRecs = [...recommendationsData];
      if (sortBy === 'highest_savings') {
        sortedRecs.sort((a, b) => ((a.savings_amount as number) || 0) - ((b.savings_amount as number) || 0));
      } else if (sortBy === 'highest_percentage') {
        sortedRecs.sort((a, b) => ((b.savings_percentage as number) || 0) - ((a.savings_percentage as number) || 0));
      } else if (sortBy === 'service') {
        sortedRecs.sort((a, b) => ((a.service as string) || '').localeCompare((b.service as string) || ''));
      }
      setRecommendations(sortedRecs);
    } catch (err: unknown) {
      console.error('Error loading compute optimization data:', err);
      setError(err instanceof Error ? err.message : 'Failed to load compute optimization data');
    } finally {
      setLoading(false);
    }
  };

  const handleServiceFilterChange = (service: string) => {
    if (service === 'all') {
      setSelectedService(null);
    } else {
      // Map frontend values to backend enum values
      const serviceMap: Record<string, string> = {
        'ec2': 'EC2',
        'ecs': 'ECS',
        'eks': 'EKS',
        'rds': 'RDS',
        'sagemaker': 'SageMaker',
      };
      setSelectedService(serviceMap[service.toLowerCase()] || service);
    }
  };

  const handleSortChange = (sortValue: string) => {
    setSortBy(sortValue);
    // Re-sort recommendations
    if (recommendations.length > 0) {
      let sortedRecs = [...recommendations];
      if (sortValue === 'highest_savings') {
        sortedRecs.sort((a, b) => ((a.savings_amount as number) || 0) - ((b.savings_amount as number) || 0));
      } else if (sortValue === 'highest_percentage') {
        sortedRecs.sort((a, b) => ((b.savings_percentage as number) || 0) - ((a.savings_percentage as number) || 0));
      } else if (sortValue === 'service') {
        sortedRecs.sort((a, b) => ((a.service as string) || '').localeCompare((b.service as string) || ''));
      }
      setRecommendations(sortedRecs);
    }
  };

  const handleTriggerScan = async () => {
    const clientId = selectedClientId || user?.client_id;
    if (!clientId || scanning) return;
    setScanning(true);
    setScanMessage(null);
    try {
      await triggerCostOptimizationScan(clientId);
      setScanMessage({ type: 'success', text: 'Scan triggered successfully. Results will appear once the scan completes (this may take a few minutes).' });
    } catch (err: unknown) {
      console.error('Error triggering scan:', err);
      setScanMessage({ type: 'error', text: err instanceof Error ? err.message : 'Failed to trigger scan' });
    } finally {
      setScanning(false);
    }
  };

  if (loading && !stats) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-blue-500"></div>
      </div>
    );
  }

  if (!tenantId) {
    return (
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-700 rounded-lg p-6">
          <h3 className="text-lg font-medium text-yellow-800 dark:text-yellow-200 mb-2">
            No Environment Available
          </h3>
          <p className="text-yellow-700 dark:text-yellow-300">
            Please configure an environment to view compute optimization data.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="mx-auto px-4 sm:px-6 lg:px-8 py-8">
      {/* Header */}
      <div className="mb-8 flex items-start justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gray-900 dark:text-white">
            Compute Optimization
          </h1>
          <p className="mt-2 text-gray-600 dark:text-gray-400">
            Identify cost-saving opportunities across EC2, ECS, EKS, RDS, and SageMaker
          </p>
        </div>
        <button
          onClick={handleTriggerScan}
          disabled={scanning}
          className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors text-sm font-medium"
        >
          {scanning ? (
            <>
              <svg className="animate-spin h-4 w-4" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
              </svg>
              Scanning...
            </>
          ) : (
            <>
              <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
              </svg>
              Trigger Scan
            </>
          )}
        </button>
      </div>

      {/* Scan Message */}
      {scanMessage && (
        <div className={`mb-6 rounded-lg p-4 flex items-center justify-between ${
          scanMessage.type === 'success'
            ? 'bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-700 text-green-800 dark:text-green-200'
            : 'bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-700 text-red-800 dark:text-red-200'
        }`}>
          <p className="text-sm">{scanMessage.text}</p>
          <button onClick={() => setScanMessage(null)} className="ml-4 text-current opacity-60 hover:opacity-100">
            <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>
      )}

      {/* Error State */}
      {error && (
        <div className="mb-6 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-700 rounded-lg p-6">
          <h3 className="text-lg font-medium text-red-800 dark:text-red-200 mb-2">
            Error Loading Data
          </h3>
          <p className="text-red-700 dark:text-red-300">{error}</p>
          <button
            onClick={loadAllData}
            className="mt-4 px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700 transition-colors"
          >
            Try Again
          </button>
        </div>
      )}

      {/* Stats Cards */}
      {stats && <ComputeStatsCards stats={stats} />}

      {/* Savings by Service */}
      {savingsByService.length > 0 && (
        <div className="mt-8">
          <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-4">
            Savings by Service
          </h2>
          <SavingsByServiceCards savings={savingsByService as { service: string; instance_count?: number | null; current_cost?: number | null; savings?: number | null }[]} />
        </div>
      )}

      {/* Optimization Recommendations */}
      <div className="mt-8">
        <OptimizationRecommendationsTable
          recommendations={recommendations as unknown as Parameters<typeof OptimizationRecommendationsTable>[0]['recommendations']}
          loading={loading}
          selectedService={selectedService}
          sortBy={sortBy}
          onServiceFilterChange={handleServiceFilterChange}
          onSortChange={handleSortChange}
        />
      </div>
    </div>
  );
};

export default ComputeOptimizationPage;
